import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { todayDateString, yesterdayDateString } from '@/api/services/riskDashboard';
import type { InstrumentProfitabilityFilterInput } from '@/api/types/instrumentProfitability';

export interface IpFilterState {
  startDate: string;
  endDate: string;
  assetClass: string | null;
  instrument: string | null;
  portfolioId: string | null;
  groupProfile: string | null;
  group: string | null;
  groupText: string | null;
  mifidClassification: string | null;
  executionVenue: string | null;
  connectorId: number | null;
  country: string | null;
  login: number | null;
  uniqueId: string | null;
  isLive: boolean;
  isConsolidated: boolean;
  showLp: boolean;
}

export function defaultIpFilters(): IpFilterState {
  const y = yesterdayDateString();
  return {
    startDate: y,
    endDate: y,
    assetClass: null,
    instrument: null,
    portfolioId: null,
    groupProfile: null,
    group: null,
    groupText: null,
    mifidClassification: null,
    executionVenue: null,
    connectorId: null,
    country: null,
    login: null,
    uniqueId: null,
    isLive: false,
    isConsolidated: false,
    showLp: false,
  };
}

export function toIpInput(f: IpFilterState): InstrumentProfitabilityFilterInput {
  return {
    startDate: f.startDate,
    endDate: f.endDate,
    isLive: f.isLive,
    isConsolidated: f.isConsolidated,
    showLp: f.showLp,
    assetClass: f.assetClass,
    connectorId: f.isConsolidated ? null : f.connectorId,
    country: f.country,
    group: f.group,
    groupText: f.groupText,
    groupProfile: f.groupProfile,
    executionVenue: f.executionVenue,
    mifidClassification: f.mifidClassification,
    portfolioId: f.isConsolidated ? null : f.portfolioId,
    instrument: f.instrument,
    login: f.isConsolidated ? null : f.login,
    uniqueId: f.isConsolidated ? f.uniqueId : null,
  };
}

interface Props {
  draft: IpFilterState;
  setDraft: React.Dispatch<React.SetStateAction<IpFilterState>>;
  onFetch: () => void;
  onModeToggle: (key: 'isLive' | 'isConsolidated' | 'showLp', value: boolean) => void;
  canConsolidated: boolean;
  fetching: boolean;
}

export function InstrumentProfitabilityFilterBar({ draft, setDraft, onFetch, onModeToggle, canConsolidated, fetching }: Props) {
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000 });
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const venues = useQuery({ queryKey: ['rd-filter', 'venues'], queryFn: filterDataApi.getExecutionVenues, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const groupProfiles = useQuery({ queryKey: ['rd-filter', 'groupProfiles'], queryFn: filterDataApi.getGroupProfiles, staleTime: 5 * 60_000 });

  const today = todayDateString();
  const str = (v: string | number | null) => (v == null ? '' : String(v));
  // Portfolio exclusivity disables the other scoping filters (legacy DISABLEABLE_FILTERS via .prop('disabled', true)):
  // connector, group, mifid, execution venue, country, asset class, login. Instrument/group-profile stay enabled.
  const locked = draft.portfolioId != null && draft.portfolioId !== '';
  const con = draft.isConsolidated;
  const lk = (node: React.ReactNode) => <div className={locked ? 'opacity-50' : undefined}>{node}</div>;

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker
            mode="range"

            maxDate={today}
            value={{ start: draft.startDate, end: draft.endDate }}
            onChange={(v) => {
              const end = v.end;
              const start = v.start > end ? end : v.start;
              setDraft((f) => ({ ...f, startDate: start, endDate: end, isLive: end === today }));
            }}
          />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    lk(
      <>
        <label className="form-label">Asset Class</label>
        {/* Changing asset class clears the instrument (legacy). */}
        <Select2Async placeholder="All" disabled={locked} value={str(draft.assetClass) || null} fetch={filterDataPaged.assetClasses} onChange={(v) => setDraft((f) => ({ ...f, assetClass: v || null, instrument: null }))} />
      </>,
    ),
    <div key="instrument">
      <label className="form-label">Instrument</label>
      <Select2Async placeholder="All" value={str(draft.instrument) || null} fetch={filterDataPaged.instruments} onChange={(v) => setDraft((f) => ({ ...f, instrument: v || null }))} />
    </div>,
    ...(!con ? [<Select2 key="pf" label="Portfolio Profile" allowClear placeholder="All" value={str(draft.portfolioId)} options={portfolios.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, portfolioId: v || null }))} />] : []),
    <Select2 key="gp" label="Group Profile" allowClear placeholder="All" value={str(draft.groupProfile)} options={groupProfiles.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, groupProfile: v || null }))} />,
    lk(
      <>
        <label className="form-label">Group</label>
        <Select2Async placeholder="All" disabled={locked} value={str(draft.group) || null} fetch={filterDataPaged.groups} onChange={(v) => setDraft((f) => ({ ...f, group: v || null }))} />
      </>,
    ),
    lk(<Select2 label="MiFID" allowClear disabled={locked} placeholder="All" value={str(draft.mifidClassification)} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, mifidClassification: v || null }))} />),
    lk(<Select2 label="Execution Venue" allowClear disabled={locked} placeholder="All" value={str(draft.executionVenue)} options={venues.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, executionVenue: v || null }))} />),
    ...(!con ? [lk(<Select2 label="Connector" allowClear disabled={locked} placeholder="All" value={str(draft.connectorId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, connectorId: v ? Number(v) : null }))} />)] : []),
    lk(
      <>
        <label className="form-label">Country</label>
        <Select2Async placeholder="All" disabled={locked} value={str(draft.country) || null} fetch={filterDataPaged.countries} onChange={(v) => setDraft((f) => ({ ...f, country: v || null }))} />
      </>,
    ),
    ...(!con
      ? [lk(
          <>
            <label className="form-label">Login</label>
            <input className="form-control" inputMode="numeric" disabled={locked} value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />
          </>,
        )]
      : [
          <div key="uid">
            <label className="form-label">Unique ID</label>
            <input className="form-control" value={draft.uniqueId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value || null }))} />
          </div>,
        ]),
  ];

  return (
    <FilterBar
      fields={fields}
      visibleCount={4}
      modes={
        <>
          <Switch label="Live Data" on={draft.isLive} onToggle={(c) => onModeToggle('isLive', c)} />
          {canConsolidated && <Switch label="By UID" on={draft.isConsolidated} onToggle={(c) => onModeToggle('isConsolidated', c)} />}
          <Switch label="Show LP" on={draft.showLp} onToggle={(c) => onModeToggle('showLp', c)} />
        </>
      }
      actions={
        <button type="button" className="btn btn-primary btn-sm" onClick={onFetch} disabled={fetching}>
          {fetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}
          Fetch
        </button>
      }
    />
  );
}

function Switch({ label, on, onToggle }: { label: string; on: boolean; onToggle: (c: boolean) => void }) {
  const id = `ip-switch-${label.replace(/\s+/g, '')}`;
  return (
    <div className="form-check form-switch mb-0">
      <input className="form-check-input" type="checkbox" role="switch" id={id} checked={on} onChange={(e) => onToggle(e.target.checked)} />
      <label className="form-check-label small" htmlFor={id}>{label}</label>
    </div>
  );
}
